import { createContext, useContext, useState, useEffect } from 'react';
import { getCountries } from '../services';
import { CountriesResponse, Country } from '../models';

export const CountryContext = createContext(
   {} as {
      countries: Country[];
      sweepstakeAllowedCountries: string[];
      loading: boolean;
      fetchCountries: () => Promise<void>;
      getCountryByCode: (code: string) => Country | undefined;
      getCountryByValue: (value: string) => Country | undefined;
      isSweepstakeCountryAllowed: (countryName: string | null | undefined) => boolean;
   }
);

export const useCountryContext = () => {
   return useContext(CountryContext);
};

export const CountryProvider = ({ children }: any) => {
   const [countries, setCountries] = useState<Country[]>([]);
   const [sweepstakeAllowedCountries, setSweepstakeAllowedCountries] = useState<string[]>([]);
   const [loading, setLoading] = useState<boolean>(true);

   const fetchCountries = async (): Promise<void> => {
      try {
         setLoading(true);
         const response = await getCountries();

         if (response.error || !response.data) {
            console.error('Failed to fetch countries:', response.error);
            // Set a default empty country for the picker
            setCountries([{ label: '', value: '', code: '', phonePrefix: '' }]);
            setSweepstakeAllowedCountries([]);
            return;
         }

         const data = response.data as CountriesResponse;
         setCountries(data.countries ?? []);
         setSweepstakeAllowedCountries(data.sweepstakeAllowedCountries ?? []);
      } catch (error) {
         console.error('Error fetching countries:', error);
         setCountries([{ label: '', value: '', code: '', phonePrefix: '' }]);
         setSweepstakeAllowedCountries([]);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchCountries();
   }, []);

   const getCountryByCode = (code: string): Country | undefined => {
      return countries.find((c) => c.code.toUpperCase() === code.toUpperCase());
   };

   const getCountryByValue = (value: string): Country | undefined => {
      return countries.find((c) => c.value.toLowerCase() === value.toLowerCase());
   };

   const isSweepstakeCountryAllowed = (countryName: string | null | undefined): boolean => {
      if (!countryName || sweepstakeAllowedCountries.length === 0) {
         return false;
      }

      const normalized = countryName.trim().toLowerCase();
      return sweepstakeAllowedCountries.some((country) => country.toLowerCase() === normalized);
   };

   return (
      <CountryContext.Provider
         value={{
            countries,
            sweepstakeAllowedCountries,
            loading,
            getCountryByCode,
            getCountryByValue,
            isSweepstakeCountryAllowed,
            fetchCountries,
         }}>
         {children}
      </CountryContext.Provider>
   );
};
